import axios from 'axios'
import { INFINITE_SCROLL_PAGE_SIZE } from '../../shared/config/constants'
import type {
	ActivityFeedResponse,
	ActivityType,
	FriendsResponse,
} from '../../shared/types/social'

export async function getActivityFeed(options: {
	types: ActivityType[]
	onlyMine?: boolean
	cursor?: string
	limit?: number
	signal?: AbortSignal
}): Promise<ActivityFeedResponse> {
	const params = new URLSearchParams()
	params.set('types', options.types.join(','))
	if (options.onlyMine) params.set('scope', 'mine')
	if (options.cursor) params.set('cursor', options.cursor)
	params.set('limit', String(options.limit ?? INFINITE_SCROLL_PAGE_SIZE))
	const response = await axios.get(`/api/activity?${params.toString()}`, {
		signal: options.signal,
	})
	return response.data as ActivityFeedResponse
}

let pendingFriendsRequest: Promise<FriendsResponse> | null = null
const pendingFriendMutations = new Set<Promise<unknown>>()

export function invalidateFriendsRequest(): void {
	pendingFriendsRequest = null
}

export async function getFriends(): Promise<FriendsResponse> {
	// Event and overlay reads during a local change share its post-mutation read.
	while (pendingFriendMutations.size) {
		await Promise.allSettled([...pendingFriendMutations])
	}
	if (pendingFriendsRequest) return pendingFriendsRequest
	const request = axios
		.get('/api/friends')
		.then((response) => response.data as FriendsResponse)
		.finally(() => {
			if (pendingFriendsRequest === request) pendingFriendsRequest = null
		})
	pendingFriendsRequest = request
	return request
}

async function mutateFriends(operation: Promise<unknown>): Promise<void> {
	pendingFriendMutations.add(operation)
	invalidateFriendsRequest()
	try {
		await operation
	} finally {
		pendingFriendMutations.delete(operation)
	}
}

export async function addFriend(username: string): Promise<void> {
	await mutateFriends(axios.post('/api/friends', { username }))
}

export async function updateFriendship(
	friendshipId: string,
	action: 'accept' | 'decline' | 'cancel' | 'remove',
): Promise<void> {
	await mutateFriends(axios.patch(`/api/friends/${friendshipId}`, { action }))
}
